<script setup>
import {ref, computed, watch} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useFormStore} from '@/stores/form'
import moment from 'moment'

import UserAvatar from '@/common/UserAvatar'
import ItemHistoryChange from './ItemHistoryChange'
import Button from '@/common/Button.vue'
import api from '@/services/api'

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

const componentsStore = useComponentsStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

const containerRef = ref(null)

const start = ref(0)
const limit = ref(5)
const loadingMore = ref(false)
const total = ref(null)
const items = ref(null)

const tableName = computed(() => formStore.value?.tableName)

const id = computed(() => formStore.value?.id)

const componentId = computed(() => {
    if (!tableName.value) return null
    return componentsStore.componentIds[tableName.value] || null
})

const isLoading = computed(() => items.value === null)

const noItemHistory = computed(() => items.value?.length === 0)

const isShowingAll = computed(() => start.value + limit.value > total.value)

const fetchItems = async () => {
    if (!componentId.value || !id.value) {
        items.value = []
        return
    }

    try {
        const response = await api.get(`api/activity/${componentId.value}/${id.value}`, {
            params: {
                start: start.value,
                limit: limit.value
            }
        })

        total.value = response?.data?.total || 0

        const currentItems = items.value || []
        currentItems.push(...(response?.data?.items || []))

        items.value = currentItems
    } catch (error) {
        console.error('Failed to fetch activity items:', error)
    } finally {
        loadingMore.value = false
    }
}

watch(
    () => [componentId.value, id.value, start.value, limit.value],
    () => {
        fetchItems()
    },
    {immediate: true}
)

watch(loadingMore, (newValue) => {
    if (!newValue && containerRef.value) {
        const el = containerRef.value.closest ? containerRef.value.closest('.offcanvas-body') : null

        if (el) {
            setTimeout(() => {
                el.scrollTo(0, el.scrollHeight)
            }, 0)
        }
    }
})

const viewMore = () => {
    loadingMore.value = true
    start.value += limit.value
}

const getDate = (timestamp) => moment(timestamp * 1000).format('DD/MM/YY')

const getTime = (timestamp) => moment(timestamp * 1000).format('HH:mm')

const getPreciseTime = (timestamp) => moment(timestamp * 1000).format('HH:mm:ss')

</script>

<template>
    <div ref="containerRef">
        <div v-if="isLoading" class="text-center">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading&hellip;</span>
            </div>
        </div>

        <div v-else-if="noItemHistory" class="text-center">No history</div>

        <div v-else>
            <div class="list-group list-group-flush">
                <div v-for="item in items" :key="item.id || item.createdDate" class="list-group-item d-flex p-0">
                    <div class="m-2 mb-0">
                        <UserAvatar :user-id="item.createdBy" :tooltip-placement="'right'"/>
                    </div>

                    <div class="flex-fill list-group list-group-flush m-0">
                        <ItemHistoryChange v-for="(oldValue, property) in item.data.old" :key="property"
                                           :form-id="formId"
                                           :property="property" :table-name="tableName" :id="id" :value="oldValue"
                                           :new-value="item.data?.new?.[property]" :old-value="oldValue"
                                           class="list-group-item px-0 visible-on-hover" style="border-style: dashed"/>
                    </div>

                    <div :title="getDate(item.createdDate) + ' @ ' + getPreciseTime(item.createdDate)" class="m-2 mb-0 mt-1 text-nowrap small text-end code">
                        {{ getDate(item.createdDate) }}
                        <br>
                        {{ getTime(item.createdDate) }}
                    </div>
                </div>
            </div>

            <div class="mt-3 text-center">
                <div v-if="!loadingMore">
                    <p v-if="isShowingAll" class="small fst-italic">Showing all</p>
                    <p v-else-if="total">Showing 1 to {{ start + limit }} of {{ total }}</p>

                    <div v-if="!isShowingAll">
                        <Button class="border" @click="viewMore">View {{ limit }} more</Button>
                    </div>
                </div>

                <div v-else class="spinner-border" role="status">
                    <span class="visually-hidden">Loading&hellip;</span>
                </div>
            </div>
        </div>
    </div>
</template>